import { useMemo } from 'react';
import { Tooltip } from '@material-ui/core';

import { SETVIImage, SText } from '@setvi/shared/components';
import { calculatePrecentage } from '@setvi/shared/utils';

import { AnalyticsProps } from 'pages/auto-rfq/store';

import {
  Container,
  IconContainer,
  Content,
  Item,
  TopContainer
} from './styled';
import AnimatedNumber from './AnimatedNumber';
import SkeletonLoader from './SkeletonLoader';

interface HeaderProps {
  total: AnalyticsProps;
  processingTime: string;
  loading?: boolean;
}

export enum AnalyticsColors {
  Matched = '#23a566',
  NonMatched = '#000000',
  Selected = '#1e96fc',
  Processed = '#ff9f29',
  MfgSkuMatched = '#FABC3F',
  NonMfgSkuMatched = '#EB5A3C',
  FoundOnline = '#3674B5',
  ExactMatch = '#15653F',
  CloseMatch = '#4DB382',
  PossibleMatch = '#F08B0F'
}

export enum AnalyticsLabels {
  Processed = 'Processed',
  Matched = 'Products Matched',
  NonMatched = 'No Match',
  Selected = 'Selected',
  ExactMatch = 'Exact Match',
  CloseMatch = 'Close Match',
  PossibleMatch = 'Possible Match',
  MfgSkuMatched = 'MFG SKU Matches',
  NonMfgSkuMatched = 'Non MFG SKU Matches',
  FoundOnline = 'Product Matches Found Online',
  MarkedAsNoMatch = 'Marked as No Match',
  RemovedItems = 'Removed Items from Quote',
  UserSelection = 'User Selection'
}

const coverNegativeCase = (number: number) => (number < 0 ? 0 : number);

const Analytics = ({ total, processingTime, loading }: HeaderProps) => {
  const items = useMemo(
    () => [
      {
        label: AnalyticsLabels.Processed,
        icon: (
          <SETVIImage src="/icons/resources.svg" alt="Processed items icon" />
        ),
        color: AnalyticsColors.Processed,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(total?.processed)}
              weight="bold"
              size="xl"
            />
            {processingTime && (
              <SText size="xl" weight="medium" title={`(${processingTime})`} />
            )}
          </Content>
        ),
        tooltip: 'The number of RFQ products scanned and the total time taken.'
      },
      {
        label: AnalyticsLabels.Matched,
        icon: (
          <SETVIImage
            src="/icons/check-circle.svg"
            alt="Matched products icon"
          />
        ),
        color: AnalyticsColors.Matched,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(total?.matched)}
              weight="bold"
              size="xl"
            />
            <SText
              size="xl"
              weight="medium"
              title={`(${calculatePrecentage(total?.matched, total?.processed, true)}%)`}
            />
          </Content>
        ),
        tooltip:
          'The number of RFQ products that have at least one match in your catalog.'
      },
      {
        label: AnalyticsLabels.ExactMatch,
        icon: <SETVIImage src="/icons/campaign.svg" alt="Exact matches icon" />,
        color: AnalyticsColors.MfgSkuMatched,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(total?.exactMatch)}
              weight="bold"
              size="xl"
            />
            <SText
              size="xl"
              weight="medium"
              title={`(${calculatePrecentage(total?.exactMatch, total?.processed, true)}%)`}
            />
          </Content>
        ),
        tooltip:
          'The number of RFQ Products with an exact product match to your catalog.'
      },
      {
        label: AnalyticsLabels.CloseMatch,
        icon: <SETVIImage src="/icons/engage.svg" alt="Close matches icon" />,
        color: AnalyticsColors.NonMfgSkuMatched,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(total?.closeMatch)}
              weight="bold"
              size="xl"
            />
            <SText
              size="xl"
              weight="medium"
              title={`(${calculatePrecentage(total?.closeMatch, total?.processed, true)}%)`}
            />
          </Content>
        ),
        tooltip:
          'The number of RFQ Products with a strong but not identical match—minor spec differences may exist.'
      },
      {
        label: AnalyticsLabels.PossibleMatch,
        icon: (
          <SETVIImage
            src="/icons/close-match.svg"
            alt="Possible matches icon"
          />
        ),
        color: AnalyticsColors.FoundOnline,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(total?.possibleMatch)}
              weight="bold"
              size="xl"
            />
            <SText
              size="xl"
              weight="medium"
              title={`(${calculatePrecentage(total?.possibleMatch, total?.processed, true)}%)`}
            />
          </Content>
        ),
        tooltip:
          'The number of RFQ Products with a tentative/low-confidence match; some details align, but need review.'
      },
      {
        label: AnalyticsLabels.NonMatched,
        icon: (
          <SETVIImage src="/icons/exclamation-circle.svg" alt="No match icon" />
        ),
        color: AnalyticsColors.NonMatched,
        content: loading ? (
          <SkeletonLoader />
        ) : (
          <Content>
            <AnimatedNumber
              value={coverNegativeCase(
                total?.notMatched + (total?.markedAsNonMatch || 0)
              )}
              weight="bold"
              size="xl"
            />
            <SText
              size="xl"
              weight="medium"
              title={`(${calculatePrecentage(total?.notMatched + (total?.markedAsNonMatch || 0), total?.processed, true)}%)`}
            />
          </Content>
        ),
        tooltip:
          'The number of RFQ Products for which no catalog match was found.'
      }
    ],
    [
      loading,
      total?.processed,
      total?.matched,
      total?.exactMatch,
      total?.closeMatch,
      total?.possibleMatch,
      total?.notMatched,
      total?.markedAsNonMatch,
      processingTime
    ]
  );

  return (
    <Container>
      {items.map(item => (
        <Tooltip key={item?.label} title={item?.tooltip} arrow>
          <Item>
            <TopContainer>
              <IconContainer>{item.icon}</IconContainer>
              <SText
                title={item.label}
                style={{ color: '#5D7083' }}
                weight="semibold"
              />
            </TopContainer>
            {item.content}
          </Item>
        </Tooltip>
      ))}
    </Container>
  );
};

export default Analytics;
